/**
 * UseCaseDetailPanel — bubble-detail panel shown BELOW IndustryPipelineOverview
 * when a Use Case bubble is clicked (Figma 20260605 nodes 2788:31074 base +
 * 2845:34960 with popovers). Layout:
 *
 *   Header: {ucName} {priorityBadge} × close
 *   4×2 KPI grid (Industry / Opps / ARR / Launch Ratio /
 *                 Pipeline平均参与深度 ⓘ / 渗透率 ⓘ / 强相关活动 ⓘ / IVT 直接参与 ⓘ)
 *   Stage breakdown — via <StageBreakdown/> shared component.
 *
 * All KPIs for a single UC are already aggregated on the {@link UcBubble}
 * (unlike Industry / Asset panels which derive influence KPIs by rolling up
 * stageInsight), so this panel reads straight from the bubble.
 */

import { type UcBubble } from './PipelineMap';
import {
  KPI_INFO_TIPS,
  PipelineDetailShell,
  StatCell,
  deriveKpisFromStages,
  deriveTopKpisFromDetail,
  launchRatioLabel,
} from './PipelineDetailShell';

// Figma 20260605 priority palette (mirrored from IndustryPipelineOverview so
// the badge on this panel matches the bubble on the chart above).
const PRIORITY_FIGMA_TAG: Record<string, { bg: string; fg: string }> = {
  strategic: { bg: '#FFF3DC', fg: '#B26A00' },
  recommended: { bg: '#E4F3FE', fg: '#0068B0' },
  opportunistic: { bg: '#EDEDED', fg: '#5F6B7A' },
};

export default function UseCaseDetailPanel({
  industry,
  bubble,
  pipelineTypeFilter,
  pipelineFilter,
  snapshotDate,
  onClose,
}: {
  industry: string;
  bubble: UcBubble;
  /** Selected value from the Pipeline类型 dropdown; undefined = All. */
  pipelineTypeFilter?: string;
  /** Property-tree JSON forwarded so the drill-in matches the bubble filter. */
  pipelineFilter?: string;
  /** Same snapshotDate driving the chart above. */
  snapshotDate?: string;
  onClose: () => void;
}) {
  const priorityKey = (bubble.priority || 'strategic') as keyof typeof PRIORITY_FIGMA_TAG;
  const tagColor = PRIORITY_FIGMA_TAG[priorityKey] ?? PRIORITY_FIGMA_TAG.strategic;
  const priorityLabel = priorityKey.charAt(0).toUpperCase() + priorityKey.slice(1);

  return (
    <PipelineDetailShell
      title={bubble.ucName}
      headerExtra={
        <span
          className="ivt-ucdp__badge"
          style={{ background: tagColor.bg, color: tagColor.fg }}
        >
          {priorityLabel}
        </span>
      }
      fetch={{ industry, ucName: bubble.ucName }}
      pipelineTypeFilter={pipelineTypeFilter}
      pipelineFilter={pipelineFilter}
      snapshotDate={snapshotDate}
      showUcNameInStages={false}
      loadingLabel="加载 stage 明细…"
      errorHeader="无法加载 opp 明细"
      onClose={onClose}
      renderKpis={(detail) => {
        // Derive all 8 KPIs from the detail response so a filter change
        // re-fetches and both halves update. Previously ALL KPIs came off
        // the bubble snapshot (captured at click time), so filter changes
        // never made it into the panel.
        const top = deriveTopKpisFromDetail(detail);
        const inf = deriveKpisFromStages(detail?.stages);
        const arrM = top ? (top.totalArr / 1e6).toFixed(2) : '—';
        // Pipeline 平均参与深度 sources from bubble.influenceDensityOpp so
        // it matches the bubble chart X-axis (Σ per-opp max_score / opp count).
        const depth = bubble.influenceDensityOpp.toFixed(2);
        return (
          <>
            <StatCell label="Industry" value={industry} />
            <StatCell label="Opps" value={top?.oppCount ?? '—'} />
            <StatCell label="ARR" value={top ? `$${arrM}M` : '—'} />
            <StatCell
              label="Launch Ratio"
              value={top ? launchRatioLabel(top.launchedArr, top.totalArr) : '—'}
            />
            <StatCell
              label="Pipeline平均参与深度"
              value={depth}
              info={KPI_INFO_TIPS.pipelineDepth}
            />
            <StatCell
              label="渗透率"
              value={inf.penetrationPct}
              info={KPI_INFO_TIPS.penetration}
            />
            <StatCell
              label="强相关opps数量"
              value={inf.stronglyTouchedOpps}
              info={KPI_INFO_TIPS.stronglyTouchedOpps}
            />
            <StatCell
              label="IVT相关opps数量"
              value={inf.touchedOpps}
              info={KPI_INFO_TIPS.touchedOpps}
            />
          </>
        );
      }}
    />
  );
}
